/**
 * OGraf `actionDurations` from the timeline phases, in integer ms (Specification, ActionDuration). Pure and zod-free
 * so the editor can show what the manifest will say while the operator edits.
 *
 * - stepCount 0 (sting): playAction lasts in + autoOutAfter + out (it resolves when the automatic out ends);
 * - otherwise playAction lasts the in phase, with per-target-step durations: step 0 = in, step n = its transition;
 * - updateAction: the longest of the update duration and the per-field sub-animations;
 * - stopAction: the out phase; customAction: its phase. Hold loops never count (they never block a promise).
 */
import type { Timeline } from './scene.ts';

export type ActionDuration =
  | { type: 'playAction'; duration: number; steps?: Array<{ step: number; duration: number }> }
  | { type: 'updateAction'; duration: number }
  | { type: 'stopAction'; duration: number }
  | { type: 'customAction'; customActionId: string; duration: number };

const ms = (n: number) => Math.max(0, Math.round(n));

export function actionDurationsOf(scene: { timeline: Timeline }): ActionDuration[] {
  const t = scene.timeline;
  const list: ActionDuration[] = [];
  if (t.steps.length === 0) {
    list.push({ type: 'playAction', duration: ms(t.in.duration + (t.autoOutAfter ?? 0) + t.out.duration) });
  } else {
    list.push({
      type: 'playAction',
      duration: ms(t.in.duration),
      steps: t.steps.map((s, i) => ({ step: i, duration: ms(i === 0 ? t.in.duration : (s.enter?.duration ?? 0)) })),
    });
  }
  const fieldPhases = Object.values(t.update.fields).map((f) => (f.mode === 'phase' ? f.phase.duration : 0));
  list.push({ type: 'updateAction', duration: ms(Math.max(t.update.duration, ...fieldPhases)) });
  list.push({ type: 'stopAction', duration: ms(t.out.duration) });
  for (const a of t.customActions) list.push({ type: 'customAction', customActionId: a.id, duration: ms(a.phase.duration) });
  return list;
}
